import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import ChatController from '../controllers/ChatController';
import {
  ClientChatTypes,
  IChannelData,
  TypeOfUser,
} from '../helpers/Constants';
import {
  IAdminCreatedChannelProperties,
  IChatTabUnreadCount,
  IClientTabUnreadCount,
  ISendbirdGroupAddMetadata,
} from '../models/ChatModels';
import { statusDispatch } from './globalActions';

const chat = 'chat';

export const chatActions = {
  createGroupChannel: `${chat}/channel/create`,
  groupChannelList: `${chat}/channel/list`,
  addGroupChannelMetadata: `${chat}/channel/metadata`,
  adminCreateGroupChannel: `${chat}/channel/admin/create`,
  adminCloseChat: `${chat}/admin/close/chat`,
  assignedChannels: `${chat}/assigned/channels`,
  setUnreadCount: `${chat}/set/unread`,
  setSelectedClient: `${chat}/set/selectedClient`,
  setAdminChannelProperties: `${chat}/set/channelProperties`,
  setChannelMetadata: `${chat}/set/channel/metadata`,
  setChatActiveTab: `${chat}/set/activeTab`,
  createGroupFromProfessional: `${chat}/create/professional/group`,
  createGroupFromPharmacy: `${chat}/create/pharmacy/group`,
  tabUnreadCount: `${chat}/unread/count`,
  clientTabUnreadCount: `${chat}/client/unread/count`,
  resetChatValues: `${chat}/reset/values`,
  searchText: `${chat}/search/text`,
  setEnteredChannelUrl: `${chat}/entered/channel`,
  pharmacyHidesChat: `${chat}/pharmacy/hide`,
  professionalHidesChat: `${chat}/professional/hide`,
};

export const createGroupChannel = createAsyncThunk(
  chatActions.createGroupChannel,
  async (
    userType: Omit<
      TypeOfUser,
      TypeOfUser.admin | TypeOfUser.pharmacyAdmin | TypeOfUser.superAdmin
    >,
    { rejectWithValue }
  ) => {
    try {
      const resp = await ChatController.createGroupChannel(userType);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const adminCreateGroupChannel = createAsyncThunk(
  chatActions.adminCreateGroupChannel,
  async (userId: string, { rejectWithValue }) => {
    try {
      const resp = await ChatController.adminCreateGroupChannel(userId);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addGroupChannelMetadata = createAsyncThunk(
  chatActions.addGroupChannelMetadata,
  async (params: ISendbirdGroupAddMetadata, { rejectWithValue }) => {
    try {
      const resp = await ChatController.addGroupMetadata(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const adminCloseChat = createAsyncThunk(
  chatActions.adminCloseChat,
  async (groupUrl: string, { rejectWithValue }) => {
    try {
      const resp = await ChatController.adminCloseChat(groupUrl);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addAssignedChannels = createAction<Array<string>>(
  chatActions.assignedChannels
);

export const createChatFromProfessional = createAsyncThunk(
  chatActions.createGroupFromProfessional,
  async (pharmacyId: string, { rejectWithValue }) => {
    try {
      const resp = await ChatController.createChatFromProfessional(pharmacyId);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const createChatFromPharmacy = createAsyncThunk(
  chatActions.createGroupFromPharmacy,
  async (professionalId: string, { rejectWithValue }) => {
    try {
      const resp = await ChatController.createChatFromPharmacy(professionalId);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const professionalHidesChat = createAsyncThunk(
  chatActions.professionalHidesChat,
  async (channelUrl: string, { rejectWithValue }) => {
    try {
      const resp = await ChatController.professionalHidesChat(channelUrl);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const pharmacyHidesChat = createAsyncThunk(
  chatActions.pharmacyHidesChat,
  async (channelUrl: string, { rejectWithValue }) => {
    try {
      const resp = await ChatController.pharmacyHidesChat(channelUrl);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const setChatSearchText = createAction<string>(chatActions.searchText);
export const setEnteredChannelUrl = createAction<string>(
  chatActions.setEnteredChannelUrl
);

export const setCreatedChannelProperties =
  createAction<IAdminCreatedChannelProperties>(
    chatActions.setAdminChannelProperties
  );

export const setUnreadCount = createAction<number>(chatActions.setUnreadCount);
export const setSelectedClient = createAction<string>(
  chatActions.setSelectedClient
);

export const setChannelMetaData = createAction<IChannelData>(
  chatActions.setChannelMetadata
);

export const setChatTabUnreadCount = createAction<IChatTabUnreadCount>(
  chatActions.tabUnreadCount
);

export const setClientTabUnreadCount = createAction<IClientTabUnreadCount>(
  chatActions.clientTabUnreadCount
);

export const setChatActiveTab = createAction<ClientChatTypes | string>(
  chatActions.setChatActiveTab
);

export const resetChatValues = createAction<Array<string>>(
  chatActions.resetChatValues
);
